import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { History } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import { preferenceService, type PreferenceAudit } from "../../api/preferenceService";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sceneTag: string;
};

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** 把 0.150 / -0.100 / null 翻译成运营口径："首选 +0.15" / "降权 -0.10" / "—"。 */
function formatBoost(v: number | null | undefined, t: TFn): string {
  if (v === null || v === undefined) return "—";
  if (Math.abs(v) < 1e-6) return t("preferenceAudit.boostNeutral");
  const prefix =
    v > 0 ? t("preferenceAudit.boostPreferredPrefix") : t("preferenceAudit.boostDemotedPrefix");
  return prefix + v.toFixed(2);
}

function actionLabel(action: PreferenceAudit["action"], t: TFn): string {
  switch (action) {
    case "insert":
      return t("preferenceAudit.actionInsert");
    case "delete":
      return t("preferenceAudit.actionDelete");
    default:
      return t("preferenceAudit.actionChange");
  }
}

export function PreferenceAuditSheet({ open, onOpenChange, sceneTag }: Props) {
  const { t } = useTranslation("customer-service");
  const { data: rows = [], isLoading } = useQuery<PreferenceAudit[]>({
    queryKey: ["kb-pref-audit", sceneTag],
    queryFn: () => preferenceService.listAudit(sceneTag || undefined),
    enabled: open,
    staleTime: 30_000,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-xl w-full">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {t("preferenceAudit.title")}
          </SheetTitle>
          <SheetDescription>
            {sceneTag
              ? t("preferenceAudit.sceneLabel", { tag: sceneTag })
              : t("preferenceAudit.globalLabel")}
            {t("preferenceAudit.descriptionSuffix")}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3 max-h-[calc(100vh-8rem)] overflow-y-auto pr-1">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t("preferenceAudit.loading")}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("preferenceAudit.empty")}</p>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="rounded-md border p-3 text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{actionLabel(r.action, t)}</span>
                  <span className="text-muted-foreground">
                    {new Date(r.createdAt).toLocaleString("zh-CN", { hour12: false })}
                  </span>
                </div>
                <div className="text-muted-foreground">
                  {t("preferenceAudit.docPrefix")}
                  {r.docId}
                </div>
                <div>
                  <span className="text-muted-foreground">{t("preferenceAudit.boostPrefix")}</span>
                  {formatBoost(r.boostBefore, t)} → {formatBoost(r.boostAfter, t)}
                </div>
                {(r.noteBefore || r.noteAfter) && (
                  <div>
                    <span className="text-muted-foreground">{t("preferenceAudit.notePrefix")}</span>
                    <span className="whitespace-pre-wrap">
                      {r.noteBefore ?? "—"} → {r.noteAfter ?? "—"}
                    </span>
                  </div>
                )}
                {r.updatedBy && (
                  <div className="text-muted-foreground">
                    {t("preferenceAudit.updatedBy", { user: r.updatedBy })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
